import styled from '@emotion/styled';
import type { MicrositeProfile } from '~/model';
import { css_gap, css_rail_down, css_rail_up, css_stroke } from '~/emotion/theme';

export default function ProfileBlockAlpha({ profiles }: { profiles: MicrositeProfile[] }) {
	return (
		<Card>
			<TopSection>
				<PresentedBy>Presented by</PresentedBy>
				<BrandRow>
					<BrandIcon viewBox="0 0 50 40" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M5 25 L20 15 L25 35 Z" fill="#38bdf8" />
						<path d="M20 15 L45 5 L45 25 L25 35 Z" fill="#0284c7" />
					</BrandIcon>
					<BrandName>TOP REALTY</BrandName>
				</BrandRow>
			</TopSection>

			{profiles.map((profile, index) => (
				<AgentSection key={index}>
					<AgentImageWrap></AgentImageWrap>
					<AgentInfo>
						<AgentName>{profile.name}</AgentName>
						<AgentCompany>{profile.company}</AgentCompany>
						{profile.phone && <AgentMeta>{profile.phone}</AgentMeta>}
						{profile.email && <AgentEmail>{profile.email}</AgentEmail>}
						{profile.license && <AgentMetaBottom>MLS# {profile.license}</AgentMetaBottom>}
						{profile.website && (
							<div>
								<WebsiteLink href={profile.website} target="_blank" rel="noopener noreferrer">
									Website
								</WebsiteLink>
							</div>
						)}
					</AgentInfo>
				</AgentSection>
			))}

			<SocialLinks>
				<SocialLink href="#">
					<i className="fab fa-facebook-f" />
				</SocialLink>
				<SocialLink href="#">
					<i className="fab fa-twitter" />
				</SocialLink>
				<SocialLink href="#">
					<i className="fab fa-linkedin-in" />
				</SocialLink>
				<SocialLink href="#">
					<i className="fab fa-pinterest-p" />
				</SocialLink>
			</SocialLinks>

			<ContactHeader>
				<ContactTitle>Get in touch</ContactTitle>
			</ContactHeader>

			<ContactForm>
				<Row>
					<Input type="text" placeholder="First*" />
					<Input type="text" placeholder="Last*" />
				</Row>
				<div>
					<FullInput type="email" placeholder="Email*" />
				</div>
				<div>
					<FullInput type="tel" placeholder="Phone #" />
				</div>
				<div>
					<Textarea placeholder="Your Message" rows={5} />
				</div>
				<ButtonRow>
					<SendButton type="button">Send Inquiry</SendButton>
				</ButtonRow>
			</ContactForm>
		</Card>
	);
}

const Card = styled.div`
	background: #fff;
	border: 1px solid ${css_stroke()};
	width: 100%;

	@media (${css_rail_up}) {
		border-top: 0;
	}

	@media (${css_rail_down}) {
		margin-top: ${css_gap('content')};
	}
`;

const TopSection = styled.div`
	padding: 1rem 1rem 2rem;
`;

const PresentedBy = styled.div`
	text-align: left;
	font-size: 12px;
	color: #1f2937;
	margin-bottom: 1.5rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
`;

const BrandRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 0.5rem;
`;

const BrandIcon = styled.svg`
	width: 2.5rem;
	height: 2.5rem;
	margin-right: 0.75rem;
`;

const BrandName = styled.span`
	font-size: 1.5rem;
	font-weight: 300;
	color: #1f2937;
	letter-spacing: 0.05em;
`;

const AgentSection = styled.div`
	border-top: 1px solid #e5e7eb;
	display: flex;
`;

const AgentImageWrap = styled.div`
	width: 125px;
	flex-shrink: 0;
`;

const AgentInfo = styled.div`
	flex: 1;
	padding: 0.75rem 0.75rem 0.75rem 1rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	font-size: 0.875rem;
`;

const AgentName = styled.div`
	font-size: 15px;
	color: #1f2937;
	margin-bottom: 0.125rem;
	letter-spacing: 0.025em;
	text-transform: uppercase;
`;

const AgentCompany = styled.div`
	font-style: italic;
	color: #4b5563;
	margin-bottom: 0.25rem;
`;

const AgentMeta = styled.div`
	color: #4b5563;
	margin-bottom: 0.125rem;
	font-size: 13px;
`;

const AgentEmail = styled.div`
	color: #64748b;
	margin-bottom: 0.125rem;
	font-size: 13px;
`;

const AgentMetaBottom = styled.div`
	color: #4b5563;
	margin-bottom: 0.5rem;
	font-size: 13px;
`;

const WebsiteLink = styled.a`
	display: inline-block;
	border: 1px solid #9ca3af;
	color: #1f2937;
	padding: 0.125rem 0.5rem;
	font-size: 11px;
	text-decoration: none;
	transition: background-color 150ms ease;

	&:hover {
		background-color: #f9fafb;
	}
`;

const SocialLinks = styled.div`
	border-top: 1px solid #e5e7eb;
	border-bottom: 1px solid #e5e7eb;
	padding: 0.875rem 0;
	display: flex;
	justify-content: center;
	gap: 1.75rem;
`;

const SocialLink = styled.a`
	color: #9ca3af;
	transition: color 150ms ease;
	font-size: 17px;
	line-height: 1;

	&:hover {
		color: #4b5563;
	}
`;

const ContactHeader = styled.div`
	padding: 1rem;
	border-bottom: 1px solid #e5e7eb;
`;

const ContactTitle = styled.h2`
	font-size: 13px;
	color: #1f2937;
	letter-spacing: 0.025em;
	text-transform: uppercase;
	margin: 0;
`;

const ContactForm = styled.div`
	padding: 1rem;
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
`;

const Row = styled.div`
	display: flex;
	gap: 0.875rem;
`;

const baseInputStyles = `
	border: 1px solid #d1d5db;
	padding: 0.5rem;
	font-size: 13px;
	outline: none;
	color: #374151;

	&::placeholder {
		color: #9ca3af;
	}

	&:focus {
		border-color: #9ca3af;
	}
`;

const Input = styled.input`
	${baseInputStyles}
	width: 50%;
`;

const FullInput = styled.input`
	${baseInputStyles}
	width: 100%;
`;

const Textarea = styled.textarea`
	${baseInputStyles}
	width: 100%;
	resize: none;
`;

const ButtonRow = styled.div`
	padding-top: 0.25rem;
`;

const SendButton = styled.button`
	border: 1px solid #9ca3af;
	color: #374151;
	padding: 0.375rem 1rem;
	font-size: 13px;
	background: transparent;
	cursor: pointer;
	transition: background-color 150ms ease;

	&:hover {
		background-color: #f9fafb;
	}
`;
